import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LIVE_URL = 'https://www.cartlens.co';
const PROD_HOSTNAMES = ['cartlens.co', 'www.cartlens.co'];
const STORAGE_KEY = 'staging_modal_dismissed_at';
const COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

const isStaging =
  typeof window !== 'undefined' &&
  !PROD_HOSTNAMES.includes(window.location.hostname) &&
  window.location.hostname !== 'localhost' &&
  !window.location.hostname.startsWith('192.168.');

function shouldShow(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    return Date.now() - parseInt(raw, 10) > COOLDOWN_MS;
  } catch {
    return true;
  }
}

function recordDismissal() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {}
}

const StagingRedirectModal: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState(() => isStaging && shouldShow());

  const dismiss = () => {
    recordDismissal();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-xl"
            onClick={dismiss}
          />

          <motion.div
            initial={{ scale: 0.92, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 16, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="relative w-full max-w-md bg-[#111827] rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

            <div className="p-8 flex flex-col gap-6">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                <ExternalLink className="text-indigo-400" size={24} />
              </div>

              <div className="flex flex-col gap-2">
                <h2 className="text-2xl font-black text-white tracking-tight leading-tight">
                  {t('staging_modal.title')}
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed font-medium">
                  {t('staging_modal.message')}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <a
                  href={LIVE_URL}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white font-black uppercase tracking-widest text-[11px] rounded-2xl transition-all"
                >
                  {t('staging_modal.cta')}
                  <ArrowRight size={16} />
                </a>
                <button
                  onClick={dismiss}
                  className="w-full px-6 py-3 text-gray-600 hover:text-gray-400 font-black uppercase tracking-widest text-[10px] transition-colors"
                >
                  {t('staging_modal.dismiss')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StagingRedirectModal;
